// Free multi-modal routing using public OSRM servers.
// No API key required. Vehicle profiles use routing.openstreetmap.de which
// runs a *separate* OSRM instance per mode, so results genuinely differ per
// vehicle. (The single router.project-osrm.org demo server returns an
// identical car route for every profile, so it is only used as a fallback.)

import type { Coordinates } from '../types';

export type TravelMode = 'car' | 'motorbike' | 'bike' | 'foot';

export interface TravelModeOption {
  id: TravelMode;
  label: string;
  description: string;
  /** Server profile path */
  profile: string;
  /** Approx top speed used for the straight-line ETA fallback (km/h) */
  fallbackSpeedKmh: number;
}

export const TRAVEL_MODES: TravelModeOption[] = [
  {
    id: 'car',
    label: 'Driving',
    description: 'Car · fastest roads',
    profile: 'car',
    fallbackSpeedKmh: 50,
  },
  {
    id: 'motorbike',
    label: 'Motorbike',
    description: 'Motorbike · faster than walking',
    profile: 'bike',
    fallbackSpeedKmh: 45,
  },
  {
    id: 'bike',
    label: 'Cycling',
    description: 'Bicycle · bike paths',
    profile: 'bike',
    fallbackSpeedKmh: 18,
  },
  {
    id: 'foot',
    label: 'Walking',
    description: 'On foot · pedestrian paths',
    profile: 'foot',
    fallbackSpeedKmh: 5,
  },
];

// Per-mode OSRM hosts. `drive` is the profile segment the OSRM API expects
// (the *host* determines the actual routing graph).
const HOSTS: Record<TravelMode, string[]> = {
  car: [
    'https://routing.openstreetmap.de/routed-car/route/v1/driving',
    'https://router.project-osrm.org/route/v1/driving',
  ],
  motorbike: [
    'https://routing.openstreetmap.de/routed-bike/route/v1/driving',
    'https://router.project-osrm.org/route/v1/driving',
  ],
  bike: [
    'https://routing.openstreetmap.de/routed-bike/route/v1/driving',
    'https://router.project-osrm.org/route/v1/cycling',
  ],
  foot: [
    'https://routing.openstreetmap.de/routed-foot/route/v1/driving',
    'https://router.project-osrm.org/route/v1/walking',
  ],
};

export interface RouteStep {
  /** e.g. "Turn left onto 5th Ave" */
  instruction: string;
  maneuverType: string;
  modifier?: string;
  name: string;
  distance: number; // meters
  duration: number; // seconds
  bearingBefore: number;
  bearingAfter: number;
  /** [lng, lat] polyline for just this step */
  coordinates: [number, number][];
}

export interface RouteLeg {
  distance: number; // meters
  duration: number; // seconds
  steps: RouteStep[];
}

export interface RouteOption {
  id: string;
  distance: number; // meters
  duration: number; // seconds
  /** full geometry as [lng, lat] pairs */
  coordinates: [number, number][];
  legs: RouteLeg[];
  steps: RouteStep[];
  weight: number;
}

export interface RouteResult {
  mode: TravelMode;
  routes: RouteOption[];
  /** snapped origin/destination actually used by the router */
  origin: Coordinates;
  destination: Coordinates;
  snappedOriginName?: string;
  snappedDestinationName?: string;
  /** true when the live OSRM servers failed and we estimated instead */
  estimated: boolean;
  provider: string;
}

export interface RouteRequest {
  origin: Coordinates;
  destination: Coordinates;
  mode: TravelMode;
  alternatives?: boolean;
  signal?: AbortSignal;
}

/** Great-circle distance in meters */
export function haversine(a: Coordinates, b: Coordinates): number {
  const R = 6371e3;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const φ1 = toRad(a.lat);
  const φ2 = toRad(b.lat);
  const dφ = toRad(b.lat - a.lat);
  const dλ = toRad(b.lng - a.lng);
  const h =
    Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function buildStep(
  raw: any,
  mode: TravelMode
): RouteStep {
  const maneuver = raw.maneuver ?? {};
  const name: string = raw.name ?? '';
  const type: string = maneuver.type ?? 'turn';
  const modifier: string = maneuver.modifier ?? '';

  const verb = describeManeuver(type, modifier, mode);
  const via = name ? ` onto ${name}` : '';

  return {
    instruction: `${verb}${via}`,
    maneuverType: type,
    modifier,
    name,
    distance: raw.distance ?? 0,
    duration: raw.duration ?? 0,
    bearingBefore: maneuver.bearing_before ?? 0,
    bearingAfter: maneuver.bearing_after ?? 0,
    coordinates: (raw.geometry?.coordinates ?? []) as [number, number][],
  };
}

function describeManeuver(type: string, modifier: string, mode: TravelMode): string {
  const dir = capitalize(modifier);
  switch (type) {
    case 'depart':
      return 'Start';
    case 'arrive':
      return 'Arrive at your destination';
    case 'turn':
      return `${dir ? dir + ' ' : ''}turn`;
    case 'continue':
      return mode === 'foot' ? 'Continue walking' : 'Continue';
    case 'new name':
      return `Continue onto ${''}`.trim() || 'Continue';
    case 'merge':
      return `${dir ? dir + ' ' : ''}merge`;
    case 'on ramp':
      return `Take the ramp${dir ? ' on the ' + modifier : ''}`;
    case 'off ramp':
      return `Take the ramp${dir ? ' off the ' + modifier : ''}`;
    case 'fork':
      return `Keep ${modifier || 'straight'} at the fork`;
    case 'roundabout':
    case 'rotary':
    case 'roundabout turn':
    case 'rotary turn':
      return `Enter the roundabout and take the ${dir ? modifier : ''} exit`;
    case 'u-turn':
      return 'Make a U-turn';
    case 'end of road':
      return `Turn ${dir ? dir.toLowerCase() : ''}`.trim();
    default:
      return capitalize(type.replace(/_/g, ' '));
  }
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

/** Straight-line fallback route when all live servers fail. */
function estimateRoute(
  origin: Coordinates,
  destination: Coordinates,
  mode: TravelMode
): RouteOption {
  const option = TRAVEL_MODES.find((m) => m.id === mode)!;
  const straight = haversine(origin, destination);
  // Real roads are ~1.25–1.4x the straight line
  const distance = straight * 1.3;
  const duration = (distance / 1000 / option.fallbackSpeedKmh) * 3600;

  return {
    id: 'estimate',
    distance,
    duration,
    coordinates: [
      [origin.lng, origin.lat],
      [destination.lng, destination.lat],
    ],
    legs: [{ distance, duration, steps: [] }],
    steps: [],
    weight: duration,
  };
}

/**
 * Fetch routes between two points for the given travel mode.
 * Tries each host in order and falls back to a straight-line estimate so the
 * UI always has something to draw.
 */
export async function getRoute({
  origin,
  destination,
  mode,
  alternatives = true,
  signal,
}: RouteRequest): Promise<RouteResult> {
  const hosts = HOSTS[mode] ?? HOSTS.car;
  const coords = `${origin.lng},${origin.lat};${destination.lng},${destination.lat}`;
  const query = new URLSearchParams({
    overview: 'full',
    geometries: 'geojson',
    steps: 'true',
    alternatives: alternatives ? 'true' : 'false',
    continue_straight: 'default',
  });

  let lastError: unknown = null;

  for (let i = 0; i < hosts.length; i++) {
    const host = hosts[i];
    try {
      const res = await fetch(`${host}/${coords}?${query.toString()}`, {
        signal,
        headers: { 'User-Agent': 'LeadMap3D/1.0 (open source map app)' },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (data.code !== 'Ok' || !Array.isArray(data.routes) || !data.routes.length) {
        throw new Error(`OSRM code=${data.code}`);
      }

      const routes: RouteOption[] = data.routes.map((r: any, idx: number) => {
        const legs: RouteLeg[] = (r.legs ?? []).map((leg: any) => ({
          distance: leg.distance ?? 0,
          duration: leg.duration ?? 0,
          steps: (leg.steps ?? []).map((s: any) => buildStep(s, mode)),
        }));

        const steps = legs.flatMap((l) => l.steps);

        return {
          id: `route-${idx}`,
          distance: r.distance ?? 0,
          duration: r.duration ?? 0,
          coordinates: (r.geometry?.coordinates ?? []) as [number, number][],
          legs,
          steps,
          weight: r.weight ?? r.duration ?? 0,
        };
      });

      // Primary route first, then shortest alternative
      routes.sort((a, b) => a.weight - b.weight);

      const wp = data.waypoints ?? [];

      return {
        mode,
        routes,
        origin: {
          lng: wp[0]?.location?.[0] ?? origin.lng,
          lat: wp[0]?.location?.[1] ?? origin.lat,
        },
        destination: {
          lng: wp[1]?.location?.[0] ?? destination.lng,
          lat: wp[1]?.location?.[1] ?? destination.lat,
        },
        snappedOriginName: wp[0]?.name || undefined,
        snappedDestinationName: wp[1]?.name || undefined,
        estimated: false,
        provider: new URL(host).hostname,
      };
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') throw err;
      lastError = err;
      // try next host
    }
  }

  console.warn('[routing] all live hosts failed, using estimate:', lastError);

  return {
    mode,
    routes: [estimateRoute(origin, destination, mode)],
    origin,
    destination,
    estimated: true,
    provider: 'offline-estimate',
  };
}

/** Format seconds as "1 h 24 min" / "45 min" */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.round((total % 3600) / 60);
  if (h > 0) return m > 0 ? `${h} h ${m} min` : `${h} h`;
  if (m > 0) return `${m} min`;
  return `${total} s`;
}

/** Format meters as "820 m" / "8.4 km" / "120 km" */
export function formatRouteDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters / 10) * 10} m`;
  if (meters < 10000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters / 1000)} km`;
}

/** Format a bearing (degrees) as a compass point */
export function bearingToCompass(bearing: number): string {
  const points = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const idx = Math.round(((bearing % 360) + 360) % 360 / 45) % 8;
  return points[idx];
}

/** Heading from device in radians -> compass degrees */
export function headingToCompass(headingRad: number | null): string {
  if (headingRad == null) return '—';
  const deg = ((headingRad * 180) / Math.PI + 360) % 360;
  return `${bearingToCompass(deg)} ${Math.round(deg)}°`;
}